import { Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { MarketGroupingConfig } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { ScrollFreezeServiceTypes } from '@frontend/sports/common/core/feature/scroll-freeze';
import { DialogAnimation, DialogSettings, ModalDialogService, ModalRef } from '@frontend/sports/modal/feature';

import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { OpenSheetViewActionPayload, SheetViewActionPayload } from './model';
import { OptionSheetViewComponent } from './option-sheet-view.component';

export enum FeatureModules {
    TopEvents = 'topevents',
    EventDetails = 'eventdetails',
    None = 'none',
}
@Injectable({ providedIn: 'root' })
export class OptionSheetViewService {
    private _modalRef: ModalRef;

    constructor(
        private marketConfig: MarketGroupingConfig,
        private modalDialogService: ModalDialogService,
        private router: Router,
    ) {}

    isCarouselEnabled(competitionId: number, sportId: number): boolean {
        const currentRouteTag = this.getRoute().data.tag;
        const module = this.getModuleFromCurrentRoute(currentRouteTag);
        const supportedSportOrCompObj = this.marketConfig.eventCarouselVisibilityConfig.find((item) => item.module === module);

        return !!supportedSportOrCompObj?.sportIds.includes(sportId) || !!supportedSportOrCompObj?.competitionIds.includes(competitionId);
    }

    private getModuleFromCurrentRoute(currentRouteTag: RouteTag): FeatureModules {
        switch (currentRouteTag) {
            case RouteTag.EventDetails:
                return FeatureModules.EventDetails;
            case RouteTag.Lobby:
            case RouteTag.Competition:
            case RouteTag.SportLobby:
            case RouteTag.MultiSportsLobby:
                return FeatureModules.TopEvents;
            default:
                return FeatureModules.None;
        }
    }

    private getRoute(): ActivatedRouteSnapshot {
        let route = this.router.routerState.snapshot.root;

        while (route.firstChild) {
            route = route.firstChild;
        }

        return route;
    }

    openSheetView(payload: OpenSheetViewActionPayload) {
        this._modalRef = this.modalDialogService.openDialog(OptionSheetViewComponent, {
            data: payload.data as Partial<OptionSheetViewComponent>,
            settings: this.getModalSettings(payload),
        });
    }

    updateSheetView(payload: SheetViewActionPayload) {
        this._modalRef?.updateData(payload);
    }

    get modelRef(): ModalRef {
        return this._modalRef;
    }

    private getModalSettings(payload: OpenSheetViewActionPayload): DialogSettings {
        const injector = Injector.create({
            providers: [{ provide: SHEET_VIEW_OPEN, useValue: true }],
        });

        return {
            cssClass: 'modal-popup option-panel-popup',
            showDialogHeader: false,
            showPageHeader: false,
            animation: DialogAnimation.SlideInFromBottom,
            closeAnimation: DialogAnimation.SlideOutFromTop,
            injector,
            scrollFreezeType: ScrollFreezeServiceTypes.All,
            headerSelector: 'ms-option-panel-header',
            gestures: true,
            scrollTargetQuerySelector: this.getScrollTargetQuerySelector(payload.data.groupType),
        };
    }

    private getScrollTargetQuerySelector(groupType?: string): string {
        let scrollTargetQuerySelector = '.option-panel-body';

        if (groupType === OptionGroupType.Period || groupType === OptionGroupType.PlayerProps) {
            scrollTargetQuerySelector += ' .overflow-y';
        }

        return scrollTargetQuerySelector;
    }
}
